<template>
  <div>
    <div class="flex items-baseline gap-3">
      <h4 class="font-semibold flex-1">{{ breakdown.label }}</h4>
      <span class="tabular-nums font-medium">{{ fmt(breakdown.displayValue, breakdown.unit_type) }}</span>
    </div>
    <p v-if="breakdown.formula" class="text-text-secondary text-xs mt-1">{{ breakdown.formula }}</p>
    <p v-if="breakdown.note" class="text-gray-500 text-[11px] mt-1 leading-snug">{{ breakdown.note }}</p>

    <div class="mt-2 divide-y divide-gray-950">
      <div v-for="operand in visibleOperands" :key="operand.key || operand.label" class="py-1.5">
        <div class="flex items-center gap-2">
          <span class="text-gray-300 flex-1 truncate">{{ operand.label }}</span>
          <span
            v-if="operand.limiting"
            class="text-[10px] uppercase tracking-wide text-danger-400 bg-gray-800 rounded px-1 py-0.5 whitespace-nowrap"
          >{{ $t('coach.stat_breakdown.biggest_gap') }}</span>
          <span
            v-if="operand.peerDelta"
            class="text-xs tabular-nums bg-gray-800 rounded px-1 py-0.5"
            :class="operand.peerDelta.better ? 'text-success-400' : 'text-danger-400'"
          >{{ formatPeerDelta(operand.peerDelta) }}</span>
          <span class="tabular-nums">{{ fmt(operand.displayValue, operand.unit_type) }}</span>
        </div>
        <p v-if="operand.subFormula" class="text-gray-500 text-[11px] mt-0.5">{{ operand.subFormula }}</p>
        <p v-if="operand.note" class="text-gray-500 text-[11px] mt-0.5 leading-snug">{{ operand.note }}</p>
      </div>
      <p v-if="hiddenOperandCount" class="text-gray-500 text-[11px] py-1.5">
        {{ $t('coach.stat_breakdown.more_inputs', hiddenOperandCount) }}
      </p>
    </div>

    <div v-if="breakdown.sign" class="flex items-baseline gap-2 border-t border-gray-950 mt-1 pt-2">
      <span class="text-text-secondary">{{ breakdown.sign }}</span>
      <span class="tabular-nums font-medium ml-auto">{{ fmt(breakdown.displayValue, breakdown.unit_type) }}</span>
    </div>
    <p v-else-if="breakdown.op === 'model'" class="text-gray-500 text-xs mt-2">{{ $t('coach.stat_breakdown.weighted_model') }}</p>

    <p v-if="breakdown.footer" class="text-gray-500 text-xs mt-2">{{ breakdown.footer }}</p>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { formatBreakdownValue } from '../../utils/formatters.js';
import { visibleOperands as pickVisibleOperands } from '../../composables/useStatBreakdown.js';

const props = defineProps({
  breakdown: { type: Object, required: true },
});

const visibleOperands = computed(() => pickVisibleOperands(props.breakdown.operands));

const hiddenOperandCount = computed(
  () => Math.max(0, props.breakdown.operands.length - visibleOperands.value.length),
);

function fmt(value, unitType) {
  if (value == null) return '—';
  return formatBreakdownValue(value, unitType);
}

function formatPeerDelta(delta) {
  return `${delta.better ? '+' : '−'}${Math.round(Math.abs(delta.percent))}%`;
}
</script>
